import { Link } from "react-router";
import { OFFERS_DISCLOSURE, type AttemptView, type EarnCoinsResponse } from "@townsy/contracts";
import { useBootstrapQuery } from "../../api/queries";
import { useEarnCoinsQuery } from "../../api/rewardsQueries";
import { isRewardsUnavailable } from "../../api/rewards";
import { formatCountdown, msUntil } from "../../lib/serverClock";
import { activeAttemptLine, dailyProgress, earnRows, earnSummary } from "../../rewards/earnList";
import { describeRewardError } from "../../rewards/errors";
import { useTownStore } from "../../state/store";
import { useServerNow } from "../checkout/useServerNow";
import { Dialog } from "../Dialog";
import { PageShell } from "../PageShell";
import { OfferListView, type OfferCardActions } from "./OfferList";
import { useRewardNav } from "./useRewardNav";

/**
 * "Earn coins" (ADR-0011 #7): every live sponsored task in the town with its reward, kind, what Townsy
 * can honestly check, the viewer's UTC-day progress (x / 100), the task in progress, and "Go there".
 * Opened from the HUD coin chip in the 3D town (a dialog), and available as the `/earn` page and in the
 * text directory (2D fallback, phones, keyboard).
 */

export const EARN_EMPTY_TEXT =
  "No sponsored tasks in town right now. Tasks appear here when a business funds and activates one; Townsy never shows invented rewards.";

export type EarnState = "loading" | "error" | "unavailable" | "ready";

export function EarnCoinsView({
  data,
  state,
  error = null,
  onRetry,
  isGuest,
  actions,
  onResume,
  now,
  inTown,
}: {
  data: EarnCoinsResponse | null;
  state: EarnState;
  error?: string | null;
  onRetry?: () => void;
  isGuest: boolean;
  actions: OfferCardActions;
  onResume(attempt: AttemptView): void;
  now: number;
  inTown: boolean;
}) {
  if (state === "loading") return <p className="muted">Loading tasks…</p>;
  if (state === "unavailable") {
    return (
      <div className="stack" style={{ gap: 8 }}>
        <p className="notice notice-info" style={{ margin: 0 }} data-testid="earn-unavailable">
          Sponsored tasks are not available on this server yet.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          {OFFERS_DISCLOSURE}
        </p>
      </div>
    );
  }
  if (state === "error" || !data) {
    return (
      <div className="notice notice-danger" role="alert">
        {error ?? "Couldn't load the tasks."}{" "}
        {onRetry ? (
          <button type="button" className="btn btn-sm" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </div>
    );
  }
  const rows = earnRows(data.items, { activeAttempt: data.activeAttempt, daily: data.daily });
  const active = data.activeAttempt && data.activeAttempt.state === "active" ? data.activeAttempt : null;
  const activeLine = active ? activeAttemptLine(active) : null;
  const left = active ? msUntil(active.expiresAt, now) : 0;
  return (
    <div className="stack earn-coins" style={{ gap: 10 }} data-testid="earn-coins">
      {isGuest ? (
        <div className="notice notice-info row" style={{ justifyContent: "space-between", margin: 0 }} data-testid="earn-guest">
          <span>Create an account to earn coins. Guests can look at the tasks but cannot collect coins.</span>
          <Link className="btn btn-sm btn-primary" to="/account">
            Create an account
          </Link>
        </div>
      ) : null}
      {active && activeLine ? (
        <div className="notice notice-success row earn-active" style={{ justifyContent: "space-between", margin: 0 }} data-testid="earn-active">
          <span>
            <strong>{activeLine}</strong>
            <span className="small"> · {left > 0 ? `${formatCountdown(left)} left` : "ending…"}</span>
          </span>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => onResume(active)} data-testid="earn-resume">
            Continue task
          </button>
        </div>
      ) : null}
      <p className="small muted" style={{ margin: 0 }} data-testid="earn-summary">
        {earnSummary(rows)}
      </p>
      <OfferListView
        rows={rows}
        disclosure={data.disclosure || OFFERS_DISCLOSURE}
        actions={actions}
        showPlace
        progress={dailyProgress(data.daily)}
        emptyText={EARN_EMPTY_TEXT}
      />
      {data.notes.length > 0 ? (
        <details className="notes">
          <summary className="small">How sponsored tasks work</summary>
          <ul className="small">
            {data.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {!inTown ? (
        <p className="small muted" style={{ margin: 0 }}>
          Coins buy home upgrades and items. They are a virtual reward: never money, never Townsy Credit.
        </p>
      ) : null}
    </div>
  );
}

/** Wires the view to the server's list and the task dialog. */
function EarnCoinsLive({ enabled }: { enabled: boolean }) {
  const boot = useBootstrapQuery();
  const isGuest = !boot.data?.session.userId;
  const q = useEarnCoinsQuery(enabled);
  const nav = useRewardNav();
  const now = useServerNow(Boolean(q.data?.activeAttempt));
  const state: EarnState = q.data ? "ready" : q.error ? (isRewardsUnavailable(q.error) ? "unavailable" : "error") : "loading";
  const actions: OfferCardActions = {
    onPrimary: (row) => nav.start(row.offer),
    onShowActive: () => {
      const a = q.data?.activeAttempt;
      if (a) nav.resume(a.attemptId);
    },
    onGoThere: nav.inTown ? (row) => nav.goThere(row.offer.propertyId) : undefined,
    goThereReason: nav.goThereReason,
    goThereHref: nav.inTown ? undefined : (row) => `/place/${encodeURIComponent(row.offer.propertyId)}`,
  };
  return (
    <EarnCoinsView
      data={q.data ?? null}
      state={state}
      error={q.error ? describeRewardError(q.error, { stage: "load" }).message : null}
      onRetry={() => void q.refetch()}
      isGuest={isGuest}
      actions={actions}
      onResume={(a) => nav.resume(a.attemptId)}
      now={now}
      inTown={nav.inTown}
    />
  );
}

/** In the 3D town: a dialog (movement stops while it is open; Escape closes it). */
export function EarnCoinsPanel() {
  const open = useTownStore((s) => s.ui.earnOpen);
  const setUi = useTownStore((s) => s.setUi);
  const isGuest = useTownStore((s) => !s.bootstrap?.session.userId);
  if (!open) return null;
  return (
    <Dialog
      title="Earn coins"
      onClose={() => setUi({ earnOpen: false })}
      wide
      footer={
        <>
          {!isGuest ? (
            <button type="button" className="btn" onClick={() => setUi({ earnOpen: false, walletOpen: true })} data-testid="earn-open-wallet">
              Open wallet
            </button>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={() => setUi({ earnOpen: false })} data-autofocus>
            Done
          </button>
        </>
      }
    >
      <EarnCoinsLive enabled={open} />
    </Dialog>
  );
}

/** `/earn`: the same list as a plain page (2D, phones, keyboard). */
export function EarnPage() {
  return (
    <PageShell title="Earn coins">
      <p className="muted" style={{ marginTop: 0 }}>
        Sponsored tasks from Townsy businesses. Each task pays coins once per account; coins buy home upgrades and items.{" "}
        <Link to="/">Back to the town</Link>.
      </p>
      <EarnCoinsLive enabled />
    </PageShell>
  );
}

/** Directory section (text directory and the 2D fallback). */
export function DirectoryTasks() {
  return (
    <section className="section" id="tasks" aria-labelledby="directory-tasks-title" data-testid="directory-tasks">
      <h2 id="directory-tasks-title">Sponsored tasks</h2>
      <EarnCoinsLive enabled />
    </section>
  );
}
